import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  apiAdmincompaniesPostBodyAddressLine1Min,
  apiAdminwaitingListsIdPatchBodyAddressLine1Max,
  apiAdminwaitingListsIdPatchBodyAddressLine2Max,
  apiAdminwaitingListsIdPatchBodyCityMax,
  apiAdminwaitingListsIdPatchBodyCompanyNameMax,
  apiAdminwaitingListsIdPatchBodyCompanyUniqueCodeMax,
  apiAdminwaitingListsIdPatchBodyEmailMax,
  apiAdminwaitingListsIdPatchBodyFirstnameMax,
  apiAdminwaitingListsIdPatchBodyLastnameMax,
  apiAdminwaitingListsIdPatchBodyPointCoeffMin,
  apiAdminwaitingListsIdPatchBodySirenMax,
  apiAdminwaitingListsIdPatchBodyTvaMax,
  apiAdminwaitingListsIdPatchBodyUserUniqueCodeMax,
  apiAdminwaitingListsIdPatchBodyZipcodeMax
} from '@r/api/generated-zod/index.ts';
import { WaitingListWaitingListWriteModelRole } from '@r/api/generated/client.schemas.ts';

export function buildWaitingListEditSchema(typologyConversionMode: boolean, t: TFunction) {
  return z
    .object({
      companyName: z
        .string()
        .max(apiAdminwaitingListsIdPatchBodyCompanyNameMax, t('company.create.name.max')),
      companyUniqueCode: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyCompanyUniqueCodeMax,
          t('company.create.unique.code.max')
        ),
      typology: z.string(),
      tva: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyTvaMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyTvaMax })
        ),
      siren: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodySirenMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodySirenMax })
        ),
      addressLine1: z
        .string()
        .max(apiAdminwaitingListsIdPatchBodyAddressLine1Max, t('company.create.address.line.1.max'))
        .refine(
          (s) => {
            const v = s.trim();
            return v.length === 0 || v.length >= apiAdmincompaniesPostBodyAddressLine1Min;
          },
          {
            message: t('form.error.char.min', {
              minChar: apiAdmincompaniesPostBodyAddressLine1Min
            })
          }
        ),
      addressLine2: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyAddressLine2Max,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyAddressLine2Max })
        ),
      zipcode: z
        .string()
        .max(apiAdminwaitingListsIdPatchBodyZipcodeMax, t('company.create.zipcode.max')),
      city: z.string().max(apiAdminwaitingListsIdPatchBodyCityMax, t('company.create.city.max')),
      countryAlpha3: z.union([
        z.literal(''),
        z.string().length(3, t('company.create.country.alpha.3.required'))
      ]),
      email: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyEmailMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyEmailMax })
        )
        .refine((s) => {
          const v = s.trim();
          return v.length === 0 || z.string().email().safeParse(v).success;
        }, t('user.create.email.invalid')),
      firstname: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyFirstnameMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyFirstnameMax })
        ),
      lastname: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyLastnameMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyLastnameMax })
        ),
      userUniqueCode: z
        .string()
        .max(
          apiAdminwaitingListsIdPatchBodyUserUniqueCodeMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsIdPatchBodyUserUniqueCodeMax })
        ),
      role: z.union([
        z.literal(''),
        z.enum([
          WaitingListWaitingListWriteModelRole.ROLE_MEMBER,
          WaitingListWaitingListWriteModelRole.ROLE_MANAGER
        ])
      ]),
      comment: z.string(),
      pointCoeff: z
        .union([
          z.coerce
            .number({
              message: t('company.create.point.coeff.invalid'),
              invalid_type_error: t('company.create.point.coeff.invalid')
            })
            .min(apiAdminwaitingListsIdPatchBodyPointCoeffMin, t('company.create.point.coeff.min')),
          z.undefined()
        ])
        .optional()
    })
    .refine(
      (data) => {
        if (typologyConversionMode || data.pointCoeff === undefined || data.pointCoeff === null) {
          return true;
        }
        const decimalPlaces = data.pointCoeff.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 3;
      },
      { message: t('company.create.point.coeff.decimal.max'), path: ['pointCoeff'] }
    );
}

export type WaitingListEditFormValues = z.infer<ReturnType<typeof buildWaitingListEditSchema>>;
